import AutoComplete from "antd/lib/auto-complete";
import Input from "antd/lib/input";
import { DefaultOptionType } from "antd/lib/select";
import classNames from "classnames";
import { BaseSyntheticEvent, useCallback, useEffect, useState } from "react";

import { NOT_FOUND_INDEX } from "rivals/shared/constants";
import styles from "./index.module.scss";
import { OwnProps, TypeaheadMatchData, TypeOnChange } from "./types";
import Spinner from "../Spinner";

const RenderLoadingLabel = (): JSX.Element => (
  <div className={styles.spinnerWrapper}>
    <Spinner />
  </div>
);

const getMatch = (searchText: string, value: string): TypeaheadMatchData => {
  const matchIndex = value.toLowerCase().indexOf(searchText.toLowerCase());

  return matchIndex !== NOT_FOUND_INDEX
    ? {
        afterMatch: value.slice(matchIndex + searchText.length),
        beforeMatch: value.slice(0, matchIndex),
        match: value.slice(matchIndex, matchIndex + searchText.length)
      }
    : {
        afterMatch: "",
        beforeMatch: value,
        match: ""
      };
};

const Typeahead = <T,>({
  RenderLabel,
  getValue,
  inputClassNames = "",
  loading,
  onChange,
  onSelect,
  placeholder,
  searchResults,
  wrapperClassName = "",
  dropdownClassName = "",
  onClear = (): void => {},
  allowClear = false,
  value,
  ...props
}: OwnProps<T>): JSX.Element => {
  type LabelType = ReturnType<typeof RenderLabel>;
  type ValueType = ReturnType<typeof getValue>;

  type SelectableOption = {
    item: T;
    label: LabelType;
    value: ValueType;
  };
  type NonSelectableOption = {
    label: LabelType;
    options: [];
  };
  type Option = SelectableOption | NonSelectableOption;

  const [options, setOptions] = useState<Option[]>([]);
  const [searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    if (!loading) {
      setOptions(
        searchResults.map(searchResult => {
          const value = getValue(searchResult);
          const match = getMatch(searchText, value);

          return {
            item: searchResult,
            label: RenderLabel(match, searchResult),
            value
          };
        })
      );
    } else {
      setOptions([
        {
          label: RenderLoadingLabel(),
          options: []
        }
      ]);
    }
  }, [searchResults, searchText, setOptions, loading]);

  const handleSearchTextChange = useCallback((e: BaseSyntheticEvent) => {
    setSearchText(e.target.value);
  }, []);

  const handleSelect = (_: string, option: DefaultOptionType): void => {
    onSelect(option.item);
  };

  return (
    <div className={classNames(styles.typeaheadWrapper, wrapperClassName)}>
      <AutoComplete
        allowClear={allowClear}
        defaultValue={props.defaultValue}
        disabled={props.disabled}
        onChange={onChange as TypeOnChange}
        onClear={onClear}
        onSelect={handleSelect}
        options={options}
        popupClassName={classNames(styles.collegeSearch, dropdownClassName)}
        value={value}
      >
        <Input
          className={inputClassNames}
          onChange={handleSearchTextChange}
          placeholder={placeholder}
          {...props}
        />
      </AutoComplete>
    </div>
  );
};

export default Typeahead;
